/**
 * The transport → session-layer event bus.
 *
 * `HttpTransport` must not import React, a router or a query client: it is
 * plain TypeScript used from tests and (later) from the service worker page
 * bridge. So the one place that *observes* a dead session — the response
 * handler — publishes a signal here, and `SessionProvider` subscribes.
 *
 * Three signals, and the distinction between the first two is load-bearing:
 *
 * - `expired` — a `401`. The session is gone; the user must re-authenticate.
 * - `unavailable` — a `503 upstream_failure`. The backend's datastore is down.
 *   The session is *not* gone, and presenting this as a sign-out is the exact
 *   failure mode the backend added the distinct status for. Show "temporarily
 *   unavailable, retrying" and keep the user signed in.
 * - `available` — the first success after an `unavailable`, so the banner can
 *   clear itself without polling.
 * - `proof` — one of the four `proof_*` 401s (docs/plan/14 §5.4) survived the
 *   transport's own retries. The session is **alive**: the backend left the
 *   cookie trio in place and kept the record. Publishing `expired` for one of
 *   these would sign a user out over a missing *device key*, destroying a live
 *   session, its cache and whatever was half-written — which is exactly the
 *   failure ADR 0042 is written against. It gets the in-place recovery dialog
 *   instead, and the transport dispatches on the code before the generic 401
 *   path (doc 10 §1.8 as amended).
 * - `expiry` — an authenticated response said when the session now ends
 *   (`X-Session-Expires`, ADR 0082). Not a problem, just news: the server
 *   slides an active session's expiry, and this is how the client's banner
 *   hears about it. Published only when the value changes.
 * - `revoked` — a `401 session_revoked`. Unlike `expired` this is not a token
 *   running out under a user who may still be typing: the owner ended this
 *   session on purpose, from somewhere else — by changing the password, or
 *   with "Sign out other sessions". The record is already deleted, so the
 *   in-place re-auth dialog is the wrong answer; this is a sign-out, and the
 *   sign-in screen says why. Dispatched on the code before the generic 401.
 */

/** Which `proof_*` code was final, so the dialog can say something true. */
export type ProofFailureReason = 'required' | 'invalid' | 'stale' | 'replayed';

/**
 * Why the server revoked this session (`details.reason` on `session_revoked`).
 *
 * `unknown` covers a missing or unrecognised reason — a newer backend, or a
 * proxy that mangled the body. It still signs out; only the copy is vaguer.
 */
export type RevocationReason = 'password_changed' | 'signed_out_elsewhere' | 'unknown';

/** Reads `details.reason` off a `session_revoked` error body, defensively. */
export function revocationReasonOf(details: unknown): RevocationReason {
  if (typeof details !== 'object' || details === null) return 'unknown';
  const reason = (details as { reason?: unknown }).reason;
  return reason === 'password_changed' || reason === 'signed_out_elsewhere' ? reason : 'unknown';
}

export type SessionSignal =
  | { readonly type: 'expired'; readonly code: string; readonly status: number }
  | { readonly type: 'unavailable'; readonly retryAfter: number | undefined }
  | { readonly type: 'available' }
  | { readonly type: 'proof'; readonly reason: ProofFailureReason; readonly code: string }
  | { readonly type: 'revoked'; readonly reason: RevocationReason }
  | { readonly type: 'expiry'; readonly expiresAt: string };

type Listener = (signal: SessionSignal) => void;

const listeners = new Set<Listener>();

/** True once an `unavailable` has been published and not yet cleared. */
let degraded = false;

export function onSessionSignal(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitSessionSignal(signal: SessionSignal): void {
  // Copy: a listener may unsubscribe itself while being notified.
  for (const listener of [...listeners]) listener(signal);
}

/** Publishes `unavailable`, at most once per outage. */
export function reportServiceUnavailable(retryAfter: number | undefined): void {
  degraded = true;
  emitSessionSignal({ type: 'unavailable', retryAfter });
}

/** Publishes `available` only on the transition back, so successes are cheap. */
export function reportServiceHealthy(): void {
  if (!degraded) return;
  degraded = false;
  emitSessionSignal({ type: 'available' });
}

/** The last expiry published, so a burst of responses is one signal. */
let lastExpiry: string | undefined;

/**
 * Publishes `expiry` when the server's word on it changes. Every
 * authenticated response carries the header, and all but a few per five
 * minutes repeat the last one.
 */
export function reportSessionExpiry(expiresAt: string): void {
  if (expiresAt === lastExpiry || !Number.isFinite(Date.parse(expiresAt))) return;
  lastExpiry = expiresAt;
  emitSessionSignal({ type: 'expiry', expiresAt });
}

/** Test seam: forget the outage latch between cases. */
export function resetServiceHealth(): void {
  degraded = false;
  lastExpiry = undefined;
}
